import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Search,
  MapPin,
  Globe2,
  History,
  X,
  Loader2,
  Compass,
  Trash2,
  Eye,
  Code2,
  BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import StreetViewModal from "./StreetViewModal";
import ApiDocsModal from "./ApiDocsModal";
import ApiStatsModal from "./ApiStatsModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ESRI_IMAGERY =
  "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";

// Camadas de referência transparentes (sem chave) sobrepostas ao satélite.
// Ruas (Esri) e nomes de lugares no idioma local (CARTO/OpenStreetMap → em
// português no Brasil), em vez dos rótulos em inglês da Esri.
const ESRI_TRANSPORT =
  "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}";
const CARTO_LABELS =
  "https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}.png";

const CONTAINER_ID = "cesiumContainer";

// Marcador no estilo Google Maps (gota vermelha com círculo branco), usado como
// imagem do billboard do Cesium no destino da busca.
const GOOGLE_PIN_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="30" height="44" viewBox="0 0 30 44">' +
  '<path d="M15 0.5C7 0.5 0.5 7 0.5 15c0 10.9 14.5 28.5 14.5 28.5S29.5 25.9 29.5 15C29.5 7 23 0.5 15 0.5z" ' +
  'fill="#EA4335" stroke="#B31412" stroke-width="1"/>' +
  '<circle cx="15" cy="15" r="5.6" fill="#ffffff"/>' +
  "</svg>";
const GOOGLE_PIN =
  "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(GOOGLE_PIN_SVG);

function shortName(displayName = "") {
  return displayName.split(",")[0] || displayName;
}

export default function EarthExplorer() {
  const viewerRef = useRef(null);
  const markerRef = useRef(null);
  const autoSpinRef = useRef(true);
  const inputRef = useRef(null);
  const skipNextSuggestRef = useRef(false);
  const currentRef = useRef(null);
  const currentSVRef = useRef(null);
  const openSVRef = useRef(null);
  const handlerRef = useRef(null);
  const recentMarkersRef = useRef(new Map());
  const svCacheRef = useRef(new Map());
  const onHoverRef = useRef(null);
  const onPinClickRef = useRef(null);
  const hoverPlaceRef = useRef(null);
  const keepCardRef = useRef(false);
  const hoverClearTimerRef = useRef(null);
  const recentPanelRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [current, setCurrent] = useState(null);
  const [recent, setRecent] = useState([]);
  const [showRecent, setShowRecent] = useState(false);
  const [streetView, setStreetView] = useState(null);
  const [svLoading, setSvLoading] = useState(false);
  const [currentSV, setCurrentSV] = useState(null);
  const [showCard, setShowCard] = useState(false);
  const [hoverPlace, setHoverPlace] = useState(null);
  const [hoverSV, setHoverSV] = useState(null);
  const [showDocs, setShowDocs] = useState(false);
  const [showStats, setShowStats] = useState(false);

  // Mantém uma referência síncrona do local atual (usada pelo Street View).
  useEffect(() => {
    currentRef.current = current;
  }, [current]);

  useEffect(() => {
    currentSVRef.current = currentSV;
  }, [currentSV]);

  useEffect(() => {
    hoverPlaceRef.current = hoverPlace;
  }, [hoverPlace]);

  // Fecha o painel "Pesquisas recentes" ao clicar/tocar fora dele
  // (vale para desktop e mobile). O botão de alternância é ignorado.
  useEffect(() => {
    if (!showRecent) return;
    const aoClicarFora = (e) => {
      const alvo = e.target;
      if (recentPanelRef.current && recentPanelRef.current.contains(alvo)) return;
      if (alvo.closest && alvo.closest('[data-testid="toggle-recent-btn"]')) return;
      setShowRecent(false);
    };
    document.addEventListener("mousedown", aoClicarFora);
    document.addEventListener("touchstart", aoClicarFora);
    return () => {
      document.removeEventListener("mousedown", aoClicarFora);
      document.removeEventListener("touchstart", aoClicarFora);
    };
  }, [showRecent]);

  /* -------------------- Cesium initialisation -------------------- */
  useEffect(() => {
    let destroyed = false;

    function initViewer() {
      const Cesium = window.Cesium;
      Cesium.Ion.defaultAccessToken = "";

      const viewer = new Cesium.Viewer(CONTAINER_ID, {
        baseLayer: false,
        baseLayerPicker: false,
        geocoder: false,
        homeButton: false,
        sceneModePicker: false,
        navigationHelpButton: false,
        animation: false,
        timeline: false,
        fullscreenButton: false,
        infoBox: false,
        selectionIndicator: false,
      });

      viewer.imageryLayers.addImageryProvider(
        new Cesium.UrlTemplateImageryProvider({
          url: ESRI_IMAGERY,
          maximumLevel: 19,
          credit:
            "Imagery: Esri, Maxar, Earthstar Geographics · Busca: OpenStreetMap (Nominatim)",
        })
      );

      // Sobreposições transparentes (sem chave). Ruas (Esri) + nomes de lugares
      // no idioma local/português (CARTO/OSM). O minimumTerrainLevel esconde os
      // rótulos quando o globo está muito afastado (evita o "South America"
      // de continente); os nomes de países/cidades surgem ao aproximar o zoom.
      const camadaRuas = new Cesium.ImageryLayer(
        new Cesium.UrlTemplateImageryProvider({
          url: ESRI_TRANSPORT,
          maximumLevel: 19,
        }),
        { minimumTerrainLevel: 3 }
      );
      viewer.imageryLayers.add(camadaRuas);

      const camadaNomes = new Cesium.ImageryLayer(
        new Cesium.UrlTemplateImageryProvider({
          url: CARTO_LABELS,
          subdomains: "abcd",
          maximumLevel: 19,
        }),
        { minimumTerrainLevel: 3 }
      );
      viewer.imageryLayers.add(camadaNomes);

      viewer.scene.globe.showGroundAtmosphere = true;
      viewer.scene.skyAtmosphere.show = true;
      viewer.scene.globe.baseColor = Cesium.Color.fromCssColorString("#0b1426");
      viewer.scene.backgroundColor = Cesium.Color.fromCssColorString("#04070f");

      // Limit how far the user can zoom in so they never go past the highest
      // available imagery LOD (which shows "Map data not yet available").
      const ctrl = viewer.scene.screenSpaceCameraController;
      // Stop manual zoom before reaching the very deep levels where Esri may not
      // have imagery (which would show "Map data not yet available").
      ctrl.minimumZoomDistance = 600;
      ctrl.maximumZoomDistance = 24000000;

      viewer.camera.setView({
        destination: Cesium.Cartesian3.fromDegrees(-20, 12, 24000000),
      });

      // Idle auto-rotation of the planet (stops on user interaction / search)
      let lastMs = performance.now();
      viewer.scene.preRender.addEventListener(() => {
        const now = performance.now();
        const dt = (now - lastMs) / 1000;
        lastMs = now;
        if (!autoSpinRef.current) return;
        viewer.scene.camera.rotate(Cesium.Cartesian3.UNIT_Z, -0.045 * dt);
      });

      const stopSpin = () => {
        autoSpinRef.current = false;
      };
      viewer.scene.canvas.addEventListener("pointerdown", stopSpin);
      viewer.scene.canvas.addEventListener("wheel", stopSpin);

      // Esconde os alfinetes que estão atrás do globo (lado oposto da Terra),
      // para que não apareçam "atravessando" o planeta ao girar o mapa. Mantém
      // os da frente sempre nítidos (sem z-fighting).
      const occluder = new Cesium.EllipsoidalOccluder(
        viewer.scene.globe.ellipsoid,
        viewer.camera.positionWC
      );
      const scratchPos = new Cesium.Cartesian3();
      const atualizarVisibilidade = (ent) => {
        if (!ent || !ent.position) return;
        const pos = ent.position.getValue(viewer.clock.currentTime, scratchPos);
        if (pos) ent.show = occluder.isPointVisible(pos);
      };
      viewer.scene.preRender.addEventListener(() => {
        occluder.cameraPosition = viewer.camera.positionWC;
        if (markerRef.current) atualizarVisibilidade(markerRef.current);
        recentMarkersRef.current.forEach(atualizarVisibilidade);
      });

      // Clique no marcador abre o Street View 360°; clique num alfinete do
      // histórico voa até aquele local.
      const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
      handler.setInputAction((movement) => {
        const picked = viewer.scene.pick(movement.position);
        if (!(Cesium.defined(picked) && picked.id)) return;
        const ent = picked.id;
        if (markerRef.current && ent === markerRef.current) {
          if (typeof openSVRef.current === "function") openSVRef.current();
        } else if (ent.__place && typeof onPinClickRef.current === "function") {
          onPinClickRef.current(ent.__place);
        }
      }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

      // Hover: cursor de "mão" + mostra o card do local (atual ou do histórico).
      handler.setInputAction((movement) => {
        const picked = viewer.scene.pick(movement.endPosition);
        let place = null;
        if (Cesium.defined(picked) && picked.id) {
          const ent = picked.id;
          if (markerRef.current && ent === markerRef.current && currentRef.current) {
            place = currentRef.current;
          } else if (ent.__place) {
            place = ent.__place;
          }
        }
        viewer.scene.canvas.style.cursor = place ? "pointer" : "";
        if (typeof onHoverRef.current === "function") onHoverRef.current(place);
      }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

      handlerRef.current = handler;

      return viewer;
    }

    const interval = setInterval(() => {
      if (
        window.Cesium &&
        window.Cesium.Viewer &&
        document.getElementById(CONTAINER_ID)
      ) {
        clearInterval(interval);
        if (destroyed) return;
        try {
          viewerRef.current = initViewer();
          setReady(true);
        } catch (e) {
          console.error("Falha ao iniciar o globo Cesium", e);
        }
      }
    }, 120);

    return () => {
      destroyed = true;
      clearInterval(interval);
      if (handlerRef.current && !handlerRef.current.isDestroyed()) {
        handlerRef.current.destroy();
      }
      handlerRef.current = null;
      if (viewerRef.current && !viewerRef.current.isDestroyed()) {
        viewerRef.current.destroy();
      }
      viewerRef.current = null;
    };
  }, []);

  /* -------------------- Camera fly-to -------------------- */
  const flyTo = useCallback((place, onArrive) => {
    const Cesium = window.Cesium;
    const viewer = viewerRef.current;
    if (!Cesium || !viewer || viewer.isDestroyed()) return;

    autoSpinRef.current = false;
    setCurrentSV(null);
    setShowCard(false);

    const lat = Number(place.lat);
    const lon = Number(place.lon);

    let span = 0.05;
    if (place.bbox && place.bbox.length === 4) {
      const [south, north, west, east] = place.bbox;
      span = Math.max(Math.abs(north - south), Math.abs(east - west));
    }
    // Closer landing zoom for a more detailed view while keeping crisp imagery.
    let height = Math.max(span * 111000 * 1.1, 1000);
    height = Math.min(height, 16000000);
    height = Math.max(height, 700);
    const pitch = span < 0.5 ? -68 : span < 4 ? -78 : -90;

    if (markerRef.current) viewer.entities.remove(markerRef.current);
    markerRef.current = viewer.entities.add({
      position: Cesium.Cartesian3.fromDegrees(lon, lat),
      billboard: {
        image: GOOGLE_PIN,
        width: 30,
        height: 44,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
      label: {
        text: shortName(place.display_name),
        font: "600 14px Manrope, sans-serif",
        fillColor: Cesium.Color.WHITE,
        showBackground: true,
        backgroundColor: Cesium.Color.fromCssColorString("rgba(7,12,24,0.85)"),
        backgroundPadding: new Cesium.Cartesian2(10, 7),
        pixelOffset: new Cesium.Cartesian2(0, -48),
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
      },
    });

    // Use a bounding sphere centered on the target so the location stays
    // perfectly centered on screen even with an oblique (angled) view.
    const center = Cesium.Cartesian3.fromDegrees(lon, lat);
    const boundingSphere = new Cesium.BoundingSphere(center, Math.max(height * 0.5, 1500));

    viewer.camera.flyToBoundingSphere(boundingSphere, {
      duration: 3.2,
      offset: new Cesium.HeadingPitchRange(
        0,
        Cesium.Math.toRadians(pitch),
        height
      ),
      complete: () => {
        if (typeof onArrive === "function") onArrive();
      },
    });

    setCurrent({ ...place, lat, lon });
  }, []);

  const resetView = useCallback(() => {
    const Cesium = window.Cesium;
    const viewer = viewerRef.current;
    if (!Cesium || !viewer || viewer.isDestroyed()) return;
    if (markerRef.current) {
      viewer.entities.remove(markerRef.current);
      markerRef.current = null;
    }
    setCurrent(null);
    setCurrentSV(null);
    setShowCard(false);
    viewer.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(-20, 12, 24000000),
      duration: 2.4,
    });
  }, []);

  /* -------------------- Data -------------------- */
  const loadRecent = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/searches/recent`, {
        params: { limit: 12 },
      });
      setRecent(data);
    } catch (e) {
      /* silencioso */
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/searches/recent`, {
          params: { limit: 12 },
        });
        if (active) setRecent(data);
      } catch (e) {
        /* silencioso */
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const saveSearch = useCallback(
    async (r) => {
      try {
        await axios.post(`${API}/searches`, {
          query: query || r.display_name,
          display_name: r.display_name,
          lat: Number(r.lat),
          lon: Number(r.lon),
          type: r.type || null,
        });
        loadRecent();
      } catch (e) {
        /* silencioso */
      }
    },
    [query, loadRecent]
  );

  // Busca (com cache) os dados do Street View de um local específico.
  const loadStreetViewFor = useCallback(async (place) => {
    if (!place) return null;
    const key = `${Number(place.lat).toFixed(5)},${Number(place.lon).toFixed(5)}`;
    if (svCacheRef.current.has(key)) return svCacheRef.current.get(key);
    try {
      const { data } = await axios.get(`${API}/streetview/find`, {
        params: { lat: Number(place.lat), lng: Number(place.lon) },
      });
      if (!data.found || !data.pano_id) {
        svCacheRef.current.set(key, null);
        return null;
      }
      const sv = {
        pano_id: data.pano_id,
        facade_heading: data.facade_heading ?? data.heading ?? 0,
        date: data.date,
        copyright: data.copyright,
        label: shortName(place.display_name),
        historico: data.historico || [],
      };
      svCacheRef.current.set(key, sv);
      return sv;
    } catch (e) {
      return null;
    }
  }, []);

  // Carrega o Street View do local atual (e guarda em currentSV para o card).
  const loadStreetView = useCallback(
    async (place) => {
      const target = place || currentRef.current;
      const sv = await loadStreetViewFor(target);
      setCurrentSV(sv);
      return sv;
    },
    [loadStreetViewFor]
  );

  // Abre o modal 360° (usa o cache currentSV; senão busca).
  const openStreetView = useCallback(async () => {
    let sv = currentSVRef.current;
    if (!sv) {
      setSvLoading(true);
      sv = await loadStreetView();
      setSvLoading(false);
      if (!sv) {
        toast.error("Sem Street View neste ponto. Tente um endereço de rua.");
        return;
      }
    }
    setStreetView(sv);
  }, [loadStreetView]);

  // Ao chegar de uma busca: carrega + abre o 360 automaticamente.
  // O card só aparece depois de fechar o 360 (ou se não houver Street View).
  const arriveStreetView = useCallback(
    async (place) => {
      setSvLoading(true);
      const sv = await loadStreetView(place);
      setSvLoading(false);
      if (sv) {
        setStreetView(sv);
      } else {
        setShowCard(true);
        toast.error("Sem Street View neste ponto. Tente um endereço de rua.");
      }
    },
    [loadStreetView]
  );

  const closeStreetView = useCallback(() => {
    setStreetView(null);
    // Revela o card (já com a miniatura da fachada) após fechar o panorama.
    if (currentRef.current) setShowCard(true);
  }, []);

  // Expõe a abertura do 360 para o clique no marcador do globo.
  useEffect(() => {
    openSVRef.current = openStreetView;
  }, [openStreetView]);

  // Abre o Street View 360° de um local arbitrário (usado pelo card de hover).
  const openSVFor = useCallback(
    async (place, sv) => {
      let data = sv;
      if (!data) {
        setSvLoading(true);
        data = await loadStreetViewFor(place);
        setSvLoading(false);
        if (!data) {
          toast.error("Sem Street View neste ponto. Tente um endereço de rua.");
          return;
        }
      }
      setStreetView(data);
    },
    [loadStreetViewFor]
  );

  // Hover sobre um alfinete -> mostra o card daquele local (com a fachada).
  const showHoverCard = useCallback(
    (place) => {
      if (!place) {
        // Já há um fechamento agendado? Não reinicia (senão nunca fecharia
        // enquanto o mouse se move sobre a área vazia do mapa).
        if (hoverClearTimerRef.current) return;
        hoverClearTimerRef.current = setTimeout(() => {
          hoverClearTimerRef.current = null;
          if (!keepCardRef.current) {
            setHoverPlace(null);
            setHoverSV(null);
          }
        }, 280);
        return;
      }
      // Sobre um alfinete: cancela qualquer fechamento pendente.
      if (hoverClearTimerRef.current) {
        clearTimeout(hoverClearTimerRef.current);
        hoverClearTimerRef.current = null;
      }
      const prev = hoverPlaceRef.current;
      if (
        prev &&
        Number(prev.lat) === Number(place.lat) &&
        Number(prev.lon) === Number(place.lon)
      ) {
        return; // mesmo alfinete
      }
      setHoverPlace(place);
      setHoverSV(null);
      loadStreetViewFor(place).then((sv) => {
        const cur = hoverPlaceRef.current;
        if (
          cur &&
          Number(cur.lat) === Number(place.lat) &&
          Number(cur.lon) === Number(place.lon)
        ) {
          setHoverSV(sv);
        }
      });
    },
    [loadStreetViewFor]
  );

  // Clique num alfinete do histórico -> voa até o local e abre o 360.
  const handlePinClick = useCallback(
    (place) => {
      skipNextSuggestRef.current = true;
      setQuery(shortName(place.display_name));
      setHoverPlace(null);
      flyTo(place, () => arriveStreetView(place));
    },
    [flyTo, arriveStreetView]
  );

  useEffect(() => {
    onHoverRef.current = showHoverCard;
  }, [showHoverCard]);

  useEffect(() => {
    onPinClickRef.current = handlePinClick;
  }, [handlePinClick]);

  // Mantém os alfinetes do histórico fincados no globo.
  useEffect(() => {
    const Cesium = window.Cesium;
    const viewer = viewerRef.current;
    if (!Cesium || !viewer || viewer.isDestroyed() || !ready) return;
    const mapa = recentMarkersRef.current;
    mapa.forEach((ent) => {
      if (!viewer.isDestroyed()) viewer.entities.remove(ent);
    });
    mapa.clear();
    recent.forEach((r) => {
      const lat = Number(r.lat);
      const lon = Number(r.lon);
      if (Number.isNaN(lat) || Number.isNaN(lon)) return;
      const ent = viewer.entities.add({
        position: Cesium.Cartesian3.fromDegrees(lon, lat),
        billboard: {
          image: GOOGLE_PIN,
          width: 26,
          height: 38,
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          // Sempre desenhado por cima da superfície (evita z-fighting/oclusão
          // que fazia os alfinetes do histórico "sumirem" no zoom de rua).
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          scaleByDistance: new Cesium.NearFarScalar(1.0e3, 1.1, 8.0e6, 0.55),
        },
      });
      ent.__place = { id: r.id, display_name: r.display_name, lat, lon };
      mapa.set(r.id, ent);
    });
  }, [recent, ready]);

  const selectResult = useCallback(
    (r, closeList = true, autoStreetView = true) => {
      skipNextSuggestRef.current = true;
      setQuery(shortName(r.display_name));
      flyTo(r, autoStreetView ? () => arriveStreetView(r) : undefined);
      if (closeList) setShowResults(false);
      saveSearch(r);
    },
    [flyTo, saveSearch, arriveStreetView]
  );

  const handleSearch = async (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    setShowResults(false);
    try {
      // 1) Geocode preciso via Google (resolve endereços com número de rua).
      try {
        const g = await axios.get(`${API}/geocode/google`, {
          params: { q },
          timeout: 12000,
        });
        if (g.data && g.data.found) {
          // Usa o endereço resolvido pelo backend (importante na busca por
          // coordenadas: mostra "Av. X, 123 - Bairro..." em vez dos números).
          const nomeResolvido =
            g.data.endereco_formatado || g.data.address || q;
          const place = {
            display_name: nomeResolvido,
            lat: g.data.lat,
            lon: g.data.lng,
          };
          skipNextSuggestRef.current = true;
          flyTo(place, () => arriveStreetView(place));
          saveSearch(place);
          return;
        }
      } catch (gErr) {
        /* cai no fallback Nominatim */
      }

      // 2) Fallback: Nominatim (lugares, cidades, pontos de interesse).
      const { data } = await axios.get(`${API}/geocode`, {
        params: { q, limit: 6 },
      });
      if (!data.results || data.results.length === 0) {
        toast.error("Nenhum local encontrado para esse endereço.");
        setResults([]);
        return;
      }
      setResults(data.results);
      selectResult(data.results[0], true, true);
    } catch (err) {
      toast.error("Não foi possível buscar o endereço. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  /* -------------------- Live autocomplete suggestions -------------------- */
  useEffect(() => {
    // Skip fetching when the query was set programmatically (e.g. recent click).
    if (skipNextSuggestRef.current) {
      skipNextSuggestRef.current = false;
      return;
    }
    const q = query.trim();
    let active = true;
    const timer = setTimeout(async () => {
      if (q.length < 3) {
        setSuggesting(false);
        return;
      }
      setSuggesting(true);
      try {
        const { data } = await axios.get(`${API}/geocode`, {
          params: { q, limit: 6 },
        });
        if (!active) return;
        const list = data.results || [];
        setResults(list);
        setShowResults(list.length > 0);
      } catch (e) {
        /* silencioso */
      } finally {
        if (active) setSuggesting(false);
      }
    }, 450);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [query]);

  const handleRecentClick = (r) => {
    skipNextSuggestRef.current = true;
    setQuery(shortName(r.display_name));
    flyTo(r, () => arriveStreetView(r));
    setShowRecent(false);
  };

  const deleteRecent = async (e, id) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/searches/${id}`);
      setRecent((prev) => prev.filter((x) => x.id !== id));
    } catch (e2) {
      /* silencioso */
    }
  };

  const clearAll = async () => {
    try {
      await axios.delete(`${API}/searches`);
      setRecent([]);
      toast.success("Histórico limpo.");
    } catch (e) {
      toast.error("Não foi possível limpar o histórico.");
    }
  };

  /* -------------------- UI -------------------- */
  // O card mostra o local em hover (alfinete) com prioridade; senão o atual.
  const cardIsHover = !!hoverPlace;
  const cardPlace = hoverPlace || (showCard ? current : null);
  const cardSV = hoverPlace ? hoverSV : currentSV;

  return (
    <div className="relative h-screen w-screen overflow-hidden" data-testid="earth-explorer">
      <div id={CONTAINER_ID} />

      {/* Loading overlay while the planet boots */}
      <AnimatePresence>
        {!ready && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-5 bg-[#04070f]"
            data-testid="globe-loading"
          >
            <div className="relative">
              <Globe2 className="h-14 w-14 text-teal-300 spin-slow" strokeWidth={1.4} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top gradient for legibility */}
      <div className="pointer-events-none fixed inset-x-0 top-0 z-10 h-44 bg-gradient-to-b from-black/70 to-transparent" />

      {/* Header + search */}
      <div className="fixed left-0 right-0 top-0 z-20 px-4 pt-4 sm:px-6 sm:pt-6">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 sm:flex-row sm:items-start sm:justify-center">
          {/* Search + actions */}
          <div className="relative w-full sm:max-w-md">
            <form
              onSubmit={handleSearch}
              className="glass flex items-center gap-2 rounded-2xl px-3 py-2 shadow-2xl shadow-black/40"
              data-testid="search-form"
            >
              <Search className="ml-1 h-5 w-5 shrink-0 text-teal-300" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => results.length && setShowResults(true)}
                placeholder="Ex.: Avenida Paulista, São Paulo"
                className="w-full bg-transparent text-sm text-white placeholder:text-slate-500 focus:outline-none"
                data-testid="search-input"
              />
              {suggesting && (
                <Loader2 className="h-4 w-4 shrink-0 spin-slow text-teal-300" />
              )}
              {query && (
                <button
                  type="button"
                  onClick={() => {
                    skipNextSuggestRef.current = true;
                    setQuery("");
                    setResults([]);
                    setShowResults(false);
                    inputRef.current?.focus();
                  }}
                  className="rounded-full p-1 text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
                  data-testid="clear-input-btn"
                  aria-label="Limpar"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              <button
                type="submit"
                disabled={loading || !query.trim()}
                className="flex shrink-0 items-center gap-1.5 rounded-xl bg-teal-400 px-3 py-1.5 text-sm font-semibold text-slate-900 transition-all hover:bg-teal-300 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="search-submit-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 spin-slow" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">Buscar</span>
              </button>
            </form>

            {/* Results dropdown */}
            <AnimatePresence>
              {showResults && results.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.18 }}
                  className="glass absolute left-0 right-0 top-full z-30 mt-2 max-h-80 overflow-y-auto rounded-2xl p-1.5 shadow-2xl shadow-black/50"
                  data-testid="search-results"
                >
                  {results.map((r, i) => (
                    <button
                      key={`${r.place_id}-${i}`}
                      onClick={() => selectResult(r)}
                      className="flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-white/8"
                      data-testid="search-result-item"
                    >
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-teal-300" />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-white">
                          {shortName(r.display_name)}
                        </span>
                        <span className="block truncate text-xs text-slate-400">
                          {r.display_name}
                        </span>
                      </span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Right control rail */}
      <div className="fixed right-4 top-1/2 z-20 flex -translate-y-1/2 flex-col gap-2.5 sm:right-6">
        <RailButton
          onClick={() => setShowRecent((s) => !s)}
          active={showRecent}
          label="Histórico"
          testid="toggle-recent-btn"
        >
          <History className="h-5 w-5" />
        </RailButton>
        <RailButton onClick={resetView} label="Ver o globo" testid="reset-view-btn">
          <Compass className="h-5 w-5" />
        </RailButton>
        <RailButton
          onClick={() => setShowDocs(true)}
          active={showDocs}
          label="Documentação da API"
          testid="toggle-docs-btn"
        >
          <Code2 className="h-5 w-5" />
        </RailButton>
        <RailButton
          onClick={() => setShowStats(true)}
          active={showStats}
          label="Métricas da API"
          testid="toggle-stats-btn"
        >
          <BarChart3 className="h-5 w-5" />
        </RailButton>
      </div>

      {/* Recent searches panel */}
      <AnimatePresence>
        {showRecent && (
          <motion.div
            ref={recentPanelRef}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 40 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            className="glass fixed right-4 top-24 z-30 w-72 rounded-2xl p-3 shadow-2xl shadow-black/50 sm:right-20 sm:top-28"
            data-testid="recent-panel"
          >
            <div className="mb-2 flex items-center justify-between px-1">
              <h3 className="font-display text-sm font-bold text-white">
                Pesquisas recentes
              </h3>
              {recent.length > 0 && (
                <button
                  onClick={clearAll}
                  className="flex items-center gap-1 text-xs text-slate-400 transition-colors hover:text-rose-300"
                  data-testid="clear-history-btn"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Limpar
                </button>
              )}
            </div>
            {recent.length === 0 ? (
              <p className="px-2 py-6 text-center text-sm text-slate-500">
                Nenhuma pesquisa ainda. Busque um endereço para começar.
              </p>
            ) : (
              <div className="max-h-[50vh] space-y-1 overflow-y-auto">
                {recent.map((r) => (
                  <div
                    key={r.id}
                    onClick={() => handleRecentClick(r)}
                    className="group flex cursor-pointer items-center gap-2.5 rounded-xl px-2.5 py-2 transition-colors hover:bg-white/8"
                    data-testid="recent-item"
                  >
                    <MapPin className="h-4 w-4 shrink-0 text-teal-300/80" />
                    <span className="min-w-0 flex-1 truncate text-sm text-slate-200">
                      {shortName(r.display_name)}
                    </span>
                    <button
                      onClick={(e) => deleteRecent(e, r.id)}
                      className="rounded-full p-1 text-slate-500 opacity-0 transition-all hover:bg-white/10 hover:text-rose-300 group-hover:opacity-100"
                      data-testid="delete-recent-btn"
                      aria-label="Remover"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Card do local (busca atual ou alfinete em hover) */}
      <AnimatePresence>
        {cardPlace && (
          <motion.div
            key={cardIsHover ? `hover-${cardPlace.lat},${cardPlace.lon}` : "current"}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ type: "spring", stiffness: 240, damping: 24 }}
            onMouseEnter={() => {
              keepCardRef.current = true;
              if (hoverClearTimerRef.current) {
                clearTimeout(hoverClearTimerRef.current);
                hoverClearTimerRef.current = null;
              }
            }}
            onMouseLeave={() => {
              keepCardRef.current = false;
              if (cardIsHover) {
                setHoverPlace(null);
                setHoverSV(null);
              }
            }}
            className="glass fixed bottom-5 left-4 z-20 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl shadow-2xl shadow-black/50 sm:left-6"
            data-testid="current-location-card"
          >
            {/* Miniatura da fachada (clicável -> abre o 360) */}
            {cardSV && (
              <button
                onClick={() => openSVFor(cardPlace, cardSV)}
                className="group relative block w-full overflow-hidden"
                data-testid="facade-thumb-btn"
                title="Abrir Street View 360°"
              >
                <img
                  src={`${API}/streetview/thumbnail?pano_id=${encodeURIComponent(
                    cardSV.pano_id
                  )}&heading=${cardSV.facade_heading ?? 0}&w=640&h=340`}
                  alt={`Fachada · ${shortName(cardPlace.display_name)}`}
                  className="h-36 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <span className="absolute bottom-2 left-2 flex items-center gap-1.5 rounded-full bg-teal-400/95 px-2.5 py-1 text-xs font-semibold text-slate-900 shadow-lg">
                  <Eye className="h-3.5 w-3.5" /> Ver em 360°
                </span>
                {cardSV.date && (
                  <span className="absolute right-2 top-2 rounded-full bg-black/55 px-2 py-0.5 text-[10px] font-medium text-slate-200 backdrop-blur">
                    {cardSV.date}
                  </span>
                )}
              </button>
            )}

            {/* Linha do tempo: galeria de capturas por ano (clicar abre o 360°) */}
            {cardSV && (cardSV.historico?.length ?? 0) > 1 && (
              <div
                className="border-t border-white/10 bg-black/25 px-3 pb-2 pt-2.5"
                data-testid="timeline-gallery"
              >
                <p className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  <History className="h-3 w-3" /> Histórico por ano
                </p>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {cardSV.historico.map((h) => (
                    <button
                      key={h.pano_id}
                      onClick={() =>
                        openSVFor(cardPlace, {
                          ...cardSV,
                          pano_id: h.pano_id,
                          date: h.date,
                          facade_heading:
                            h.facade_heading ?? cardSV.facade_heading,
                        })
                      }
                      className={`group/hist relative w-20 shrink-0 overflow-hidden rounded-lg border transition-all hover:scale-[1.03] ${
                        h.atual
                          ? "border-teal-300/70 ring-1 ring-teal-300/40"
                          : "border-white/10 hover:border-teal-300/60"
                      }`}
                      data-testid={`timeline-item-${h.date}`}
                      title={`Abrir 360° de ${h.date}`}
                    >
                      <img
                        src={`${API}/streetview/thumbnail?pano_id=${encodeURIComponent(
                          h.pano_id
                        )}&heading=${
                          h.facade_heading ?? cardSV.facade_heading ?? 0
                        }&w=160&h=100`}
                        alt={`Fachada em ${h.date}`}
                        className="h-12 w-full object-cover transition-transform duration-300 group-hover/hist:scale-110"
                        loading="lazy"
                      />
                      <span
                        className={`absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent px-1 pb-0.5 pt-3 text-center text-[10px] font-bold ${
                          h.atual ? "text-teal-300" : "text-white"
                        }`}
                      >
                        {h.ano || h.date}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="p-4">
              <p className="truncate font-display text-base font-bold text-white" data-testid="current-name">
                {shortName(cardPlace.display_name)}
              </p>
              <p className="mt-0.5 line-clamp-2 text-xs text-slate-400">
                {cardPlace.display_name}
              </p>
              <p className="mt-1.5 font-mono text-xs text-teal-300/90" data-testid="current-coords">
                {Number(cardPlace.lat).toFixed(5)}, {Number(cardPlace.lon).toFixed(5)}
              </p>
              <button
                onClick={() => openSVFor(cardPlace, cardSV)}
                disabled={svLoading}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-teal-400 px-3 py-2 text-sm font-semibold text-slate-900 transition-all hover:bg-teal-300 disabled:cursor-not-allowed disabled:opacity-60"
                data-testid="open-streetview-btn"
              >
                {svLoading ? (
                  <Loader2 className="h-4 w-4 spin-slow" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
                {svLoading ? "Buscando 360°..." : "Street View 360°"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Street View 360 overlay */}
      <AnimatePresence>
        {streetView && (
          <StreetViewModal
            api={API}
            pano={streetView}
            thumbnail={
              streetView.pano_id
                ? `${API}/streetview/thumbnail?pano_id=${encodeURIComponent(
                    streetView.pano_id
                  )}&heading=${streetView.facade_heading ?? 0}&w=1024&h=576&fov=100`
                : null
            }
            onClose={closeStreetView}
          />
        )}
      </AnimatePresence>

      {/* Documentação da API */}
      <AnimatePresence>
        {showDocs && (
          <ApiDocsModal api={API} onClose={() => setShowDocs(false)} />
        )}
      </AnimatePresence>

      {/* Métricas da API */}
      <AnimatePresence>
        {showStats && (
          <ApiStatsModal api={API} onClose={() => setShowStats(false)} />
        )}
      </AnimatePresence>
    </div>
  );
}

function RailButton({ children, onClick, label, active, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      title={label}
      className={`glass grid h-11 w-11 place-items-center rounded-2xl text-slate-200 shadow-lg shadow-black/40 transition-all hover:scale-105 hover:text-teal-200 ${
        active ? "ring-1 ring-teal-300/60 text-teal-200" : ""
      }`}
    >
      {children}
    </button>
  );
}
